<template>
  <!-- Illustrated variant: large centered icon + message (+ optional sub-message + CTA slot).
       Activated only when `icon` is provided; otherwise the inline-alert path below is
       byte-identical to the original component so existing call sites are unaffected. -->
  <div v-if="icon" class="empty-state-container flex flex-col items-center gap-3 py-8 text-center">
    <i class="ti" :class="[`ti-${icon}`, iconSize, `color-${color}`]" aria-hidden="true"></i>
    <div class="m-0" :class="[messageSizeClass, `color-${color}`]">{{ primaryText }}</div>
    <div v-if="subMessage" class="m-0 text-xxs color-medium-dark">{{ subMessage }}</div>
    <slot />
  </div>
  <!-- This branch only runs when `icon` is empty, so it needs its own fallback glyph —
       passing `icon` straight through would render no icon at all. -->
  <CustomInlineAlert
    v-else-if="!$slots.default"
    :title="title ?? 'Nothing here yet'"
    :message="primaryText"
    :icon="inlineIcon"
    :color="color"
    :size="size"
  />
  <div v-else class="empty-state-container">
    <CustomInlineAlert
      :title="title ?? 'Nothing here yet'"
      :message="primaryText"
      :icon="inlineIcon"
      :color="color"
      :size="size"
    />
    <slot />
  </div>
</template>
<script lang="ts">
import CustomInlineAlert from "@/components/states/CustomInlineAlert.vue";
import { ComponentColorType, ComponentSizeType } from "@/types/componentType";
import { PropType, computed, defineComponent } from "vue";

export default defineComponent({
  name: "NoRecordFound",
  components: { CustomInlineAlert },
  props: {
    title: { type: String, default: null },
    noRecordsText: { type: String, default: "No matching records found." },
    message: { type: String, default: null },
    subMessage: { type: String, default: null },
    icon: { type: String, default: "" },
    iconSize: { type: String, default: "text-xxl" },
    color: { type: String as PropType<ComponentColorType>, default: "medium" },
    size: { type: String as PropType<ComponentSizeType>, default: "default" },
  },
  setup(props) {
    const primaryText = computed(() => props.message ?? props.noRecordsText);
    const inlineIcon = computed(() => props.icon || "inbox");
    const messageSizeClass = computed(() =>
      props.size === "small" ? "text-xs" : props.size === "large" ? "text-m" : "text-s",
    );
    return { primaryText, inlineIcon, messageSizeClass };
  },
});
</script>
